// ═══════════════════════════════════════════════════════════
// IncidentSelectorPill — standalone incident switcher
// Used in module PageHeaders (below subtitle) and wherever else needed.
// ═══════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import { IconChevronDown } from '@tabler/icons-react';
import { useAuthStore } from '../../auth/auth-store';

export function IncidentSelectorPill() {
  const activeIncident  = useAuthStore(s => s.activeIncident);
  const incidents       = useAuthStore(s => s.incidents);
  const setActiveIncident = useAuthStore(s => s.setActiveIncident);

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className={`scope-pill${activeIncident ? ' incident-mode' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-label="Switch incident"
        style={{ fontSize: 12, height: 28 }}
      >
        {activeIncident ? (
          <>
            <span className="scope-pill-dot-wrap">
              <span className={`incident-chip-dot${activeIncident.status === 'active' ? ' active' : ''}`} />
              {activeIncident.status === 'active' && <span className="incident-chip-dot-ring" />}
            </span>
            <span className="scope-pill-label">{activeIncident.name}</span>
            <span className="scope-pill-meta">{activeIncident.status?.toUpperCase()}</span>
          </>
        ) : (
          <>
            <span className="scope-pill-workspace-icon">⊞</span>
            <span className="scope-pill-label">No Incident</span>
            <span className="scope-pill-meta">Select →</span>
          </>
        )}
        <IconChevronDown size={12} style={{ marginLeft: 4, color: 'var(--color-neutral-500)', flexShrink: 0 }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 2000,
          minWidth: 240, background: '#fff',
          border: '1px solid var(--color-neutral-200)',
          borderRadius: 4,
          boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
        }}>
          {/* Workspace option */}
          <button
            onClick={() => { setActiveIncident(null); setOpen(false); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              width: '100%', padding: '7px 16px', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
              background: !activeIncident ? 'var(--color-primary-50)' : 'transparent',
              borderBottom: '1px solid var(--color-neutral-100)',
              textAlign: 'left',
            }}
            onMouseEnter={e => { if (activeIncident) e.currentTarget.style.background = 'var(--color-neutral-50)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = !activeIncident ? 'var(--color-primary-50)' : 'transparent'; }}
          >
            <span style={{ width: 7, height: 7, borderRadius: 4, background: 'var(--color-primary-700)', flexShrink: 0, display: 'inline-block' }} />
            <span style={{ flex: 1, fontSize: 14, fontWeight: !activeIncident ? 600 : 400, color: !activeIncident ? 'var(--color-primary-800)' : 'var(--color-neutral-800)' }}>Agency-wide</span>
            {!activeIncident && <span style={{ fontSize: 12, color: 'var(--color-primary-700)', fontWeight: 700 }}>✓</span>}
          </button>

          {/* Incidents */}
          <div style={{ padding: '5px 14px 3px', fontSize: 12, fontWeight: 700, color: 'var(--color-neutral-400)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            Incidents
          </div>
          {incidents.length === 0 ? (
            <div style={{ padding: '8px 14px 8px', fontSize: 12, color: 'var(--color-neutral-400)', fontStyle: 'italic' }}>No incidents on file</div>
          ) : incidents.map(inc => {
            const isActive = activeIncident?.id === inc.id;
            const dotColor = inc.status === 'active' ? 'var(--color-error)' : 'var(--color-neutral-400)';
            return (
              <div key={inc.id} style={{ display: 'flex', alignItems: 'center' }}>
                <button
                  onClick={() => { setActiveIncident(inc); setOpen(false); }}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 16px', border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                    background: isActive ? 'var(--color-primary-50)' : 'transparent',
                    textAlign: 'left',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--color-neutral-50)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = isActive ? 'var(--color-primary-50)' : 'transparent'; }}
                >
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, flexShrink: 0, display: 'inline-block' }} />
                  <span style={{ flex: 1, fontSize: 14, fontWeight: isActive ? 600 : 400, color: isActive ? 'var(--color-primary-800)' : 'var(--color-neutral-800)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {inc.name}
                  </span>
                  {isActive && <span style={{ fontSize: 12, color: 'var(--color-primary-700)', fontWeight: 700, flexShrink: 0 }}>✓</span>}
                </button>
              </div>
            );
          })}

        </div>
      )}
    </div>
  );
}
